import { fetchWithAuth } from './api';

// API base URL from environment variable
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * Create a Stripe Connect account for a convention owner
 * @param {Object} data - Account data
 * @param {string} data.userId - User ID
 * @param {string} data.email - Email address
 * @param {string} data.businessName - Business name (optional)
 * @returns {Promise<Object>} - Response with accountId and accountLink
 */
export const createStripeConnectAccount = async (data) => {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/connect/create-account`,
      {
        method: 'POST',
        body: data,
      }
    );
    return response;
  } catch (error) {
    console.error('Error creating Stripe account:', error);
    throw error;
  }
};

/**
 * Get the status of a Stripe Connect account
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Account status details
 */
export const getStripeAccountStatus = async (userId) => {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/connect/account-status/${userId}`,
      {
        method: 'GET',
      }
    );
    return response;
  } catch (error) {
    // Check if this is a "No Stripe account found" error (404) or other expected errors
    if (error.message && (error.message.includes('404') || error.message.includes('400'))) {
      // Return null instead of throwing for expected "not found" scenarios
      // (These are normal for users who haven't set up Stripe Connect)
      return null;
    } else {
      console.error('Error getting account status:', error);
      throw error;
    }
  }
};

/**
 * Get payment settings for a convention owner
 * @param {string} ownerId - Convention owner's user ID
 * @returns {Promise<Object>} - Payment settings
 */
export const getStripePaymentSettings = async (ownerId) => {
  try {
    console.log('Fetching payment settings for owner:', ownerId);
    
    const response = await fetchWithAuth(
      `${API_BASE_URL}/connect/payment-settings/${ownerId}`,
      {
        method: 'GET',
      }
    );
    
    console.log('Payment settings response:', response);
    
    if (response && response.settings) {
      return response.settings;
    }
    
    // Return default settings if none found
    return {
      achEnabled: false,
      achPreference: 'card_only',
      achMinimumAmount: 0
    };
  } catch (error) {
    // Log the error but don't throw - always return default settings
    if (error.message && error.message.includes('400')) {
      console.warn('Payment settings not configured for owner (400 error), using defaults');
    } else {
      console.warn('Error getting payment settings, using defaults:', error.message);
    }
    
    // Return default settings on error - never throw
    return {
      achEnabled: false,
      achPreference: 'card_only',
      achMinimumAmount: 0
    };
  }
};

/**
 * Create a new account link for onboarding
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Response with accountLink URL
 */
export const createStripeAccountLink = async (userId) => {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/connect/create-account-link`,
      {
        method: 'POST',
        body: { userId },
      }
    );
    return response;
  } catch (error) {
    console.error('Error creating account link:', error);
    throw error;
  }
};

/**
 * Disconnect a Stripe account
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Success response
 */
export const disconnectStripeAccount = async (userId) => {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/connect/disconnect`,
      {
        method: 'POST',
        body: { userId },
      }
    );
    return response;
  } catch (error) {
    console.error('Error disconnecting account:', error);
    throw error;
  }
};

/**
 * Update Stripe payment settings for a convention owner
 * @param {Object} data - Settings data
 * @param {string} data.userId - User ID
 * @param {Object} data.settings - Payment settings object
 * @param {boolean} data.settings.achEnabled - Whether ACH payments are enabled
 * @param {string} data.settings.achPreference - ACH preference ('both', 'ach_only', 'card_only', 'ach_preferred', 'threshold')
 * @param {number} data.settings.achMinimumAmount - Minimum amount for ACH payments when using threshold preference
 * @returns {Promise<Object>} - Response with success status
 */
export const updateStripePaymentSettings = async (data) => {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/connect/update-payment-settings`,
      {
        method: 'POST',
        body: data,
      }
    );
    return response;
  } catch (error) {
    console.error('Error updating payment settings:', error);
    throw error;
  }
};

/**
 * Create a payment intent
 * @param {Object} data - Payment data
 * @param {number} data.amount - Amount in cents
 * @param {string} data.eventId - Event ID
 * @param {string} data.conventionOwnerId - Convention owner's user ID
 * @param {Object} data.registrationData - Registration data
 * @param {string} data.customerId - Stripe customer ID (optional)
 * @param {string} data.paymentMethod - Payment method ID (optional)
 * @param {string} data.paymentMethodType - Payment method type ('card', 'us_bank_account', or undefined for default)
 * @returns {Promise<Object>} - Response with clientSecret and paymentIntentId
 */
export const createPaymentIntent = async (data) => {
  try {
    console.log("Creating payment intent with data:", JSON.stringify(data, null, 2));
    
    // Validate required fields
    const missingFields = [];
    if (!data.amount) missingFields.push('amount');
    if (!data.eventId) missingFields.push('eventId');
    if (!data.conventionOwnerId) missingFields.push('conventionOwnerId');
    if (!data.registrationData) missingFields.push('registrationData');
    
    if (missingFields.length > 0) {
      throw new Error(`Missing required parameters: ${missingFields.join(', ')}`);
    }
    
    // Validate amount
    if (typeof data.amount !== 'number') {
      data.amount = parseInt(data.amount, 10);
      if (isNaN(data.amount) || data.amount <= 0) {
        throw new Error("Amount must be a positive number");
      }
    }
    
    // Validate eventId
    if (!data.eventId || data.eventId === 'NaN' || data.eventId === 'undefined' || data.eventId === 'null') {
      console.error("Invalid eventId:", data.eventId);
      throw new Error("Invalid event ID");
    }
    
    // Validate conventionOwnerId
    if (!data.conventionOwnerId || data.conventionOwnerId === 'undefined' || data.conventionOwnerId === 'null') {
      console.error("Invalid conventionOwnerId:", data.conventionOwnerId);
      throw new Error("The event owner's ID is missing or invalid");
    }
    
    // Validate registration data
    if (!data.registrationData.name) {
      console.error("Missing customer name in registration data");
      throw new Error("Customer information is incomplete");
    }
    
    // Add payment method types if specified
    if (data.paymentMethodType) {
      data.payment_method_types = [data.paymentMethodType];
    }
    
    const response = await fetchWithAuth(
      `${API_BASE_URL}/payment/create-intent`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );
    
    if (!response || !response.clientSecret) {
      throw new Error("Invalid response from payment service");
    }
    
    console.log("Payment intent created successfully:", response);
    return response;
  } catch (error) {
    console.error("Error creating payment intent:", error);
    
    // Enhance error message based on common issues
    if (error.message && error.message.includes('No Stripe account found')) {
      throw new Error(`No Stripe account found for the event owner (ID: ${data.conventionOwnerId}). The event owner needs to connect their Stripe account to accept payments.`);
    }
    
    throw error;
  }
};

/**
 * Enhances an event object with payment settings from the convention owner
 * @param {Object} event - The event object
 * @returns {Promise<Object>} - Enhanced event with payment settings
 */
export const enhanceEventWithPaymentSettings = async (event) => {
  try {
    if (!event) {
      console.error('Cannot enhance event: Event is null or undefined');
      return event;
    }
    
    // Check if the event already has payment settings
    if (event.paymentSettings) {
      console.log('Event already has payment settings:', event.paymentSettings);
      return event;
    }
    
    // Check if the event has a convention owner ID
    if (!event.conventionOwnerId) {
      console.warn('Event has no convention owner ID, cannot fetch payment settings');
      
      // Add default payment settings
      event.paymentSettings = {
        achEnabled: false,
        achPreference: 'card_only',
        achMinimumAmount: 0
      };
      
      return event;
    }
    
    console.log('Enhancing event with payment settings for owner:', event.conventionOwnerId);
    
    // Fetch payment settings for the convention owner with additional error handling
    try {
      const paymentSettings = await getStripePaymentSettings(event.conventionOwnerId);
      event.paymentSettings = paymentSettings;
      console.log('Enhanced event with payment settings:', event.paymentSettings);
    } catch (paymentError) {
      console.warn('Failed to fetch payment settings, using defaults:', paymentError.message);
      event.paymentSettings = {
        achEnabled: false,
        achPreference: 'card_only',
        achMinimumAmount: 0
      };
    }
    
    return event;
  } catch (error) {
    console.error('Error enhancing event with payment settings:', error);
    
    // Add default payment settings on error
    event.paymentSettings = {
      achEnabled: false,
      achPreference: 'card_only',
      achMinimumAmount: 0
    };
    
    return event;
  }
}; 